import '@ribajs/types'

import { Riba, coreModule, LifecycleService } from '@ribajs/core'
import { EventDispatcher } from '@ribajs/events'
import { routerModule } from '@ribajs/router'

import { TwModule } from './modules/tailwind/index.js'
import {
  ScrollbarDraggableBinder,
  ScrollEventsBinder,
  TouchEventsBinder,
} from '@ribajs/extras'
import { InlineScriptComponent } from '@ribajs/extras/src/components/inline-script/inline-script.component.js'
import { ShopifyCartCountBinder, ShopifyImgBinder } from '@ribajs/shopify'

import { ready } from '@ribajs/utils'
import { doNotTrackAnyExternAnchor, detectBrowser } from './utils/index.js'

import './services/shopify-consent.service.js'
import * as SBComponents from './components/index.js'
import * as SbFormatters from './formatter/index.js'
import * as SBBinder from './binders/index.js'

import type { State } from '@ribajs/history'
import { ToasterService } from './services/toaster.service.js'
import { ScrollingService } from './services/scrolling.service.js'
import { Breakpoint } from './modules/tailwind/types/breakpoint.js'
import { seebergerGlobals } from '../globals.js'
import { AnalyticsService } from './services/analytics.service.js'

const browser = detectBrowser()

const riba = new Riba()

riba.module.register(coreModule.init())
riba.module.register(routerModule.init())
riba.module.register(
  TwModule.init({
    // Please keep this in sync with the tailwind.config.cjs (theme.extend.screens)
    breakpoints: [
      {
        dimension: 0,
        name: 'xxs',
      },

      // Convert Tailwind screen config for JS use
      ...Object.entries(seebergerGlobals.tailwindScreens).reduce<Breakpoint[]>(
        (agg, [name, dimension]) => {
          if (dimension) {
            agg.push({
              name,
              dimension,
            })
          }
          return agg
        },
        [],
      ),
    ],
  }),
)
ToasterService.init()
ScrollingService.init()

// Selected binders
riba.module.binder.registerAll([
  ShopifyCartCountBinder,
  ShopifyImgBinder,
  ScrollbarDraggableBinder,
  ScrollEventsBinder,
  TouchEventsBinder,
])

// Custom components, formatters and binders
riba.module.component.registerAll(SBComponents)
riba.module.formatter.registerAll(SbFormatters)
riba.module.binder.registerAll(SBBinder)

const lifecycle = LifecycleService.getInstance()
const routerEvents = new EventDispatcher('main')

routerEvents.on('initStateChange', () => {
  document.body.classList.remove('page-complete', 'page-ready')
  document.body.classList.add('page-loading')
})
routerEvents.on('transitionCompleted', () => {
  document.body.classList.remove('page-loading')
  document.body.classList.add('page-complete')
})
routerEvents.on(
  'newPageReady',
  async (
    _viewId: string,
    _currentStatus: State,
    _prevStatus: State | null,
    _container: HTMLElement | null,
    _newPageRawHTML: string,
    _dataset: any,
    firstPageLoad: boolean,
  ) => {
    if (window.debug) console.info('[Riba.js] New page ready')

    if (!firstPageLoad) {
      // Forward event to WebPixel for analytics tracking
      AnalyticsService.pushToDataLayer({
        event: 'page_viewed',
        page_title: document.title,
      })
    }

    doNotTrackAnyExternAnchor()

    // WORKAROUND for Safari srcset not updated on new page using client side router
    if (browser.isSafari && window.router_enable) {
      const images = document.querySelectorAll('img')
      for (const img of Array.from(images)) {
        if (!img.src || img.src.includes('reload') || img.src.includes('.svg'))
          continue
        const src = img.src
        const url = new URL(src, window.location.href)
        url.searchParams.append('reload', '1')
        img.src = url.href
      }
    }

    document.body.classList.remove('page-loading')
    document.body.classList.add('page-ready')
  },
)

lifecycle.events.on('ComponentLifecycle:allBound', () => {
  if (window.debug) console.info('[Riba.js] All components bound 👍')
})

lifecycle.events.on('ComponentLifecycle:error', (error: Error) => {
  if (window.debug)
    console.warn('[Riba.js] Error on bound components 🫤', error)
})

lifecycle.events.on('ComponentLifecycle:noComponents', () => {
  if (window.debug) console.warn('[Riba.js] No component to bound found ❓')
})

ready(async () => {
  const view = riba.bind(document.body, {})

  // Register components from the beginning to avoid flickering (by default the components are registered when they are first used)
  view.registerComponent(SBComponents.SbLogoComponent)
  view.registerComponent(SBComponents.SbCartComponent)
  view.registerComponent(SBComponents.SbMarqueeToggleComponent)

  view.registerComponent(SBComponents.SbVariantOverlayComponent)
  // view.registerComponent(SBComponents.SbAddToFavoritesComponent)
  view.registerComponent(SBComponents.SbSeoContentComponent)
  view.registerComponent(SBComponents.SbDynamicSectionsComponent)
  view.registerComponent(SBComponents.SbToasterComponent)
  view.registerComponent(SBComponents.SbToastComponent)
  view.registerComponent(SBComponents.SbIconComponent)

  view.registerComponent(InlineScriptComponent)

  // Smooth scroll behavior is broken / not supported in Safari, so we use a polyfill
  // We need this polyfill for the slideshow component
  // See https://github.com/iamdustan/smoothscroll
  const smoothscrollSupported =
    'scrollBehavior' in document.documentElement.style
  if (!smoothscrollSupported) {
    const smoothscroll = await import('smoothscroll-polyfill')
    smoothscroll.polyfill()
  }

  console.info('[Riba.js] Up and running 🚀')
})

await import('./new-components.js')
await import('../custom-elements/deferred-media.js')
;(() => {
  // (Temporary?) Kolsquare integration - #68759-2
  if (window) {
    const urlParams = new URLSearchParams(window?.location?.search)
    const sales_token = urlParams.get('ts')
    if (sales_token) {
      window.localStorage.setItem('kolsquare', JSON.stringify({ sales_token }))
    }
  }
})()
